import React, { useContext, useState, useCallback } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import HorizontalScroll from '@prisjakt/ui-horizontal-scroll';
import {
  NavigationLink as NavigationLinkType,
  NavigationSlice as NavigationSliceProps,
} from '../../../../types';
import RouteContext from '../../../../routes/context';
import Viewport from '../../../../components/viewport';
import { trackProductCollectionFacetNavigation } from '../../tracking';
import NavigationLink from './link';

import * as S from './styles';

const SHOW_ALL_LIMIT = 8;

const renderLinks = (
  links: NavigationLinkType[] | null | undefined,
  filter: any,
  onClick: (link: NavigationLinkType) => void,
  showAll = true,
  asListItems = false
) =>
  links && links.length
    ? links.map((link, index) => {
        const selected = filter && filter.id === link.id;
        const Component: React.ComponentType<S.ListItemProps> = asListItems
          ? S.ListItem
          : S.ListItemDiv;
        const visible = index < SHOW_ALL_LIMIT || showAll;
        return link.path ? (
          <NavigationLink
            key={link.value}
            link={link}
            visible={visible}
            selected={selected}
            onClick={onClick}
            // Lazy loading is disabled for all initially visible images to enable them
            // to load before React is loaded. The goal of this strategy is to improve
            // our Lighthouse performance score, and specifically the "Largest Contentful
            // Paint" metric.
            lazyLoadImage={index >= SHOW_ALL_LIMIT}
            Component={Component}
          />
        ) : null;
      })
    : null;

const NavigationSlice = (props: NavigationSliceProps) => {
  const { links, property } = props;
  const { dispatch, getState } = useContext(RouteContext);
  const { t } = useContext(AppContext);
  const [showAll, setShowAll] = useState(false);
  const onShowAll = useCallback(() => setShowAll(!showAll), [
    showAll,
    setShowAll,
  ]);
  const filter = (getState().filters || []).find(
    (f: { property: String }) => f.property === property
  );
  const onClick = useCallback(
    (link: NavigationLinkType) => {
      trackProductCollectionFacetNavigation(
        getState().filters,
        property || '',
        link.id
      );
      dispatch({
        type: 'FILTER_LIST_UPDATE',
        property,
        id: link.id,
      });
    },
    [getState, property, dispatch]
  );

  const mobileLinks = renderLinks(links, filter, onClick);
  const desktopLinks = renderLinks(links, filter, onClick, showAll, true);

  return mobileLinks || desktopLinks ? (
    <S.Wrapper>
      {mobileLinks && (
        <Viewport mobile tablet>
          <HorizontalScroll
            itemWidth={110}
            loadingHeight={140}
            showArrows={false}
            itemHint
          >
            {mobileLinks}
          </HorizontalScroll>
        </Viewport>
      )}
      {desktopLinks && (
        <Viewport desktop>
          <S.ListWrapper>
            <S.List>{desktopLinks}</S.List>
            {links && links.length > SHOW_ALL_LIMIT && (
              <S.ListAction>
                <S.ListActionButton onClick={onShowAll}>
                  {t(
                    'common',
                    showAll ? 'button.show-less' : 'button.show-more'
                  )}
                </S.ListActionButton>
              </S.ListAction>
            )}
          </S.ListWrapper>
        </Viewport>
      )}
    </S.Wrapper>
  ) : null;
};

export default NavigationSlice;
